import io from 'socket.io-client';
import { createLogger } from './logger';

const SOCKET_PATH = '/api/socket.io';

/**
 * SignalingClient — thin, event-driven wrapper around Socket.IO.
 *
 * Responsibilities:
 * - connect + transparent reconnect (the infra kills WebSockets at ~60s;
 *   on every reconnect we re-register the session via `rejoin` so the
 *   server re-binds the room to the new socket id — the P2P call survives).
 * - relay WebRTC signals, matchmaking, reveal/hide, leave.
 */
export class SignalingClient {
  constructor(backendUrl, sessionId) {
    this.log = createLogger('signaling');
    this.backendUrl = backendUrl;
    this.sessionId = sessionId;
    this.socket = null;
    this.handlers = new Map();
    this.hasConnectedOnce = false;
  }

  on(event, handler) {
    this.handlers.set(event, handler);
  }

  emitLocal(event, data) {
    const handler = this.handlers.get(event);
    if (handler) handler(data);
  }

  connect() {
    if (this.socket) return;
    this.socket = io(this.backendUrl, {
      path: SOCKET_PATH,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 2000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      const isReconnect = this.hasConnectedOnce;
      this.log('connected', { socketId: this.socket.id, reconnect: isReconnect });
      if (isReconnect) {
        this.socket.emit('rejoin', { session_id: this.sessionId });
        this.emitLocal('reconnected');
      } else {
        this.hasConnectedOnce = true;
        this.emitLocal('ready');
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.log('socket-dropped', { reason, note: 'auto-reconnect will re-register; P2P call unaffected' });
    });

    this.socket.on('connect_error', (err) => {
      this.log('connect-error', { error: err.message });
    });

    const relay = [
      'searching',
      'match_found',
      'signal',
      'peer_revealed',
      'peer_hidden',
      'peer_left',
      'peer_socket_updated',
      'rejoined',
      'rejoin_failed',
      'stats_update',
      'moderation_warning',
      'banned',
      'peer_reaction',
      'peer_chat_message',
    ];
    relay.forEach((event) => {
      this.socket.on(event, (data) => {
        if (event !== 'stats_update') this.log(`recv:${event}`, data || {});
        this.emitLocal(event, data);
      });
    });
  }

  joinMatchmaking(interests = []) {
    this.log('send:join_matchmaking', { sessionId: this.sessionId });
    this.socket?.emit('join_matchmaking', { session_id: this.sessionId, interests });
  }

  sendSignal(targetSid, signal) {
    this.socket?.emit('signal', { target: targetSid, signal });
  }

  reveal(roomId) {
    this.log('send:reveal_video', { roomId });
    this.socket?.emit('reveal_video', { room_id: roomId });
  }

  hide(roomId) {
    this.log('send:hide_video', { roomId });
    this.socket?.emit('hide_video', { room_id: roomId });
  }

  leaveRoom() {
    this.log('send:leave_room');
    this.socket?.emit('leave_room', {});
  }

  sendReaction(emoji) {
    this.socket?.emit('reaction', { emoji });
  }

  sendChatMessage(text) {
    this.socket?.emit('chat_message', { text });
  }

  disconnect() {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.log('disconnected-by-client');
  }
}
